import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { DbCheckbox } from '../DbCheckbox';
import DbAmountDisplay from '../DbAmountDisplay';
import DbButton from '../DbButton';
import type { DbCartFooterProps } from './DbCartFooter.types';

/**
 * DbCartFooter - 购物车底部操作栏组件
 *
 * 功能：
 * - 支持结算模式和管理模式切换
 * - 全选/取消全选功能（使用 DbCheckbox 组件）
 * - 显示总价和选中数量（使用 DbAmountDisplay 组件）
 * - 结算/批量删除操作按钮（使用 DbButton 组件）
 */
const DbCartFooter: React.FC<DbCartFooterProps> = ({
  variant = 'settlement',
  isAllSelected = false,
  selectedCount = 0,
  totalPrice = 0,
  pricePrefix = '¥',
  totalLabel = '合计:',
  selectAllLabel = '全选',
  settlementText = '结算({count})',
  deleteText = '删除({count})',
  primaryColor = '#FF2442',
  checkColor = '#FF2442',
  uncheckColor = '#ccc',
  disabled = false,
  onSelectAll,
  onAction,
  style,
  priceStyle,
  showBorder = true,
  borderColor = '#f0f0f0',
  safeAreaInset = true,
}) => {
  const insets = useSafeAreaInsets();

  /**
   * 格式化按钮文字
   */
  const formatButtonText = (template: string, count: number) => {
    return template.replace('{count}', String(count));
  };

  /**
   * 获取按钮文字
   */
  const getButtonText = () => {
    if (variant === 'manage') {
      return formatButtonText(deleteText, selectedCount);
    }
    return formatButtonText(settlementText, selectedCount);
  };

  /**
   * 获取按钮自定义样式
   */
  const getButtonCustomStyle = () => {
    const isDisabled = disabled || selectedCount === 0;
    return {
      backgroundColor: isDisabled ? '#ccc' : primaryColor,
      borderRadius: 20,
      paddingHorizontal: 16,
      paddingVertical: 8,
      minWidth: 90,
    };
  };

  return (
    <View
      style={[
        styles.container,
        showBorder && { borderTopColor: borderColor },
        safeAreaInset && { paddingBottom: Math.max(insets.bottom, 8) },
        style,
      ]}
    >
      {/* 左侧：全选按钮（使用 DbCheckbox） */}
      <View style={styles.leftSection}>
        <DbCheckbox
          checked={isAllSelected}
          onChange={onSelectAll}
          label={selectAllLabel}
          layout="left"
          size="small"
          activeColor={checkColor}
          inactiveColor={uncheckColor}
        />
      </View>

      {/* 右侧：价格和操作按钮区域 */}
      <View style={styles.rightSection}>
        {/* 价格信息（仅在结算模式下显示） */}
        {variant === 'settlement' && (
          <View style={styles.priceContainer}>
            <Text style={styles.totalLabel}>{totalLabel}</Text>
            <DbAmountDisplay
              amount={totalPrice}
              currency={pricePrefix}
              mainFontSize={18}
              mainColor={primaryColor}
              style={priceStyle}
            />
          </View>
        )}

        {/* 操作按钮 */}
        <DbButton
          title={getButtonText()}
          variant="primary"
          disabled={disabled || selectedCount === 0}
          onPress={onAction}
          style={getButtonCustomStyle()}
          textStyle={{ color: '#fff', fontSize: 14, fontWeight: '500' }}
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#fff',
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderTopWidth: 1,
  },
  leftSection: {
    flexDirection: 'row',
    alignItems: 'center',
    width: 110,
  },
  rightSection: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 12,
  },
  priceContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 4,
  },
  totalLabel: {
    fontSize: 13,
    color: '#333',
    marginRight: 4,
  },
});

export default DbCartFooter;
